import { useQuery } from "@tanstack/react-query";
import { Eye, Gavel, PoundSterling, ShieldCheck, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatGBP } from "@/lib/format";
import { cn } from "@/lib/utils";

type Auction = {
  id: string;
  current_bid_pence: number | null;
  starting_price_pence: number;
  reserve_price_pence: number | null;
  bid_count: number | null;
};

function Stat({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Gavel;
  label: string;
  value: string;
  tone?: "gold" | "emerald" | "amber";
}) {
  const toneClass =
    tone === "gold"
      ? "text-gold"
      : tone === "emerald"
        ? "text-emerald-400"
        : tone === "amber"
          ? "text-amber-400"
          : "text-foreground";
  return (
    <div className="rounded-xl border border-border/60 bg-surface-2/40 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3 w-3" aria-hidden /> {label}
      </div>
      <p className={cn("mt-1 font-display text-lg tabular-nums", toneClass)}>{value}</p>
    </div>
  );
}

/**
 * Compact activity summary for an auction. Uses only existing auction columns
 * plus the aggregate watcher-count function (counts only, no identities).
 */
export function ActivitySummary({ auction }: { auction: Auction }) {
  const { data: watchers } = useQuery({
    queryKey: ["auction-watchers", auction.id],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_auction_watcher_count", {
        _auction_id: auction.id,
      });
      if (error) throw error;
      return Number(data ?? 0);
    },
  });

  const high = auction.current_bid_pence ?? 0;
  const reserveMet = auction.reserve_price_pence ? high >= auction.reserve_price_pence : null;

  return (
    <section className="mt-6" aria-label="Auction activity">
      <h2 className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">
        Auction activity
      </h2>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <Stat
          icon={TrendingUp}
          label="High bid"
          value={high > 0 ? formatGBP(high) : "—"}
          tone="gold"
        />
        <Stat
          icon={PoundSterling}
          label="Starting price"
          value={formatGBP(auction.starting_price_pence)}
        />
        <Stat icon={Gavel} label="Valid bids" value={String(auction.bid_count ?? 0)} />
        <Stat
          icon={Eye}
          label="Watching"
          value={watchers === undefined ? "—" : String(watchers)}
        />
        <Stat
          icon={ShieldCheck}
          label="Reserve"
          value={reserveMet === null ? "No reserve" : reserveMet ? "Met" : "Not met"}
          tone={reserveMet === null ? undefined : reserveMet ? "emerald" : "amber"}
        />
      </div>
    </section>
  );
}
